import { useState } from "react";
import { useFormat } from "../../lib/format";

/**
 * "5 minutes ago", in the viewer's language, with the absolute time — in
 * their zone and format — as the visible title and in the element for
 * assistive technology. `never` is what to show for a missing timestamp.
 */
export function RelativeTime({ iso, never = "never", now }: { iso?: string | null; never?: string; now?: number }) {
  // Read the clock once per mount: a relative label that re-derives on
  // every render is unstable, and one a minute stale is not wrong.
  const [mountedAt] = useState(() => Date.now());
  const format = useFormat();
  if (!iso) return <span className="text-muted">{never}</span>;
  const then = Date.parse(iso);
  const reference = now ?? mountedAt;
  const rtf = new Intl.RelativeTimeFormat(format.locale, { numeric: "auto" });
  const diff = (then - reference) / 1000;
  const abs = Math.abs(diff);
  const rel =
    abs < 60 ? rtf.format(Math.round(diff), "second") : abs < 3_600 ? rtf.format(Math.round(diff / 60), "minute") : abs < 86_400 ? rtf.format(Math.round(diff / 3_600), "hour") : rtf.format(Math.round(diff / 86_400), "day");
  const absolute = format.when(iso);
  return (
    <time dateTime={iso} title={absolute}>
      {rel}
      <span className="sr-only"> ({absolute})</span>
    </time>
  );
}
